import React, { useState, useEffect } from 'react';
import { FaTimes, FaBuilding, FaFileInvoice, FaMapMarkerAlt, FaToggleOn, FaToggleOff, FaUser, FaPhone, FaBriefcase, FaPlus, FaTrash } from 'react-icons/fa';
import { SelectWithSearch } from '../../utils/select-with-search';
import { countries } from '../../utils/countries';
import { SearchableSelect } from '../../utils/select-with-search';
import axiosInstance from '../../libs/axios';

interface ClientRepresentative {
  id?: number;
  contact_person: string;
  contact_number: string;
  designation: string;
  designation_id?: number | null;
  representative_location: string;
  client_id?: number;
  created_at?: string;
  updated_at?: string;
  country_code?: string;
}

interface ClientStateCircle {
  id: number;
  state_name: string;
  circle_name?: string;
}

interface Client {
  id: number;
  client_full_name: string;
  client_short_name: string;
  address: string;
  gst_number: string;
  state: string;
  state_circle_id?: number | null;
  state_circle?: ClientStateCircle | null;
  is_active: boolean;
  created_by: number;
  suspended_by?: number | null;
  suspended_at?: string | null;
  created_at: string;
  updated_at: string;
  representatives?: ClientRepresentative[];
  client_projects?: any[];
}

interface EditClientFormData {
  id: number;
  client_full_name: string;
  client_short_name: string;
  address: string;
  gst_number: string;
  state: string;
  state_circle_id?: number | '';
  is_active?: boolean;
  representatives?: ClientRepresentative[];
}

interface EditClientOffcanvasProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: EditClientFormData) => void;
  client: Client | null;
  loading?: boolean;
}

interface DesignationOption {
  value: number;
  label: string;
}

interface CountryOption {
  value: string;
  label: string;
  flag: string;
}

export const EditClientOffcanvas: React.FC<EditClientOffcanvasProps> = ({
  isOpen,
  onClose,
  onSubmit,
  client,
  loading = false
}) => {
  const [formData, setFormData] = useState<EditClientFormData>({
    id: 0,
    client_full_name: '',
    client_short_name: '',
    address: '',
    gst_number: '',
    state: '',
    state_circle_id: '',
    is_active: true,
    representatives: []
  });

  const [errors, setErrors] = useState<Partial<Omit<EditClientFormData, 'id' | 'is_active' | 'representatives'>>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [representativeErrors, setRepresentativeErrors] = useState<Record<number, Partial<ClientRepresentative>>>({});

  // Dynamic data states
  const [designationOptions, setDesignationOptions] = useState<DesignationOption[]>([]);
  const [designationLoading, setDesignationLoading] = useState(false);
  const [designationPage, setDesignationPage] = useState(1);
  const [designationHasMore, setDesignationHasMore] = useState(true);
  const [designationSearch, setDesignationSearch] = useState("");

  const [selectedState, setSelectedState] = useState<any | null>(null);
  const [selectedCountryCodes, setSelectedCountryCodes] = useState<Record<number, any | null>>({});
  const [selectedDesignations, setSelectedDesignations] = useState<Record<number, any | null>>({});
  const [deletedRepresentativeIds, setDeletedRepresentativeIds] = useState<number[]>([]);

  // Country options
  const countryOptions: CountryOption[] = countries.map(country => ({
    value: country.code,
    label: `${country.flag} ${country.code}`,
    flag: country.flag
  }));

  // Fetch designations
  const fetchDesignations = async (search: string = "", page: number = 1, append: boolean = false) => {
    setDesignationLoading(true);
    try {
      const response = await axiosInstance.get('/designations/list', {
        params: {
          search: search.trim() || undefined,
          page,
          limit: 20,
          status: 'active'
        }
      });

      if (response.data.success) {
        const newDesignations = response.data.data.designations.map((d: any) => ({
          value: d.id,
          label: d.name
        }));

        if (append) {
          setDesignationOptions(prev => [...prev, ...newDesignations]);
        } else {
          setDesignationOptions(newDesignations);
        }

        setDesignationHasMore(response.data.data.pagination.has_next_page);
      }
    } catch (error) {
      console.error('Error fetching designations:', error);
    } finally {
      setDesignationLoading(false);
    }
  };

  const handleDesignationSearch = (search: string) => {
    setDesignationSearch(search);
    setDesignationPage(1);
    fetchDesignations(search, 1, false);
  };

  const updateRepresentativeDesignation = (index: number, option: DesignationOption | null) => {
    setFormData(prev => ({
      ...prev,
      representatives: prev.representatives?.map((rep, i) =>
        i === index
          ? {
              ...rep,
              designation: option ? option.label : "",
              designation_id: option ? option.value : null,
            }
          : rep
      ) || [],
    }));

    setSelectedDesignations(prev => ({
      ...prev,
      [index]: option,
    }));

    if (representativeErrors[index]?.designation) {
      setRepresentativeErrors(prev => ({
        ...prev,
        [index]: {
          ...prev[index],
          designation: undefined,
        },
      }));
    }
  };

  const updateRepresentativeCountryCode = (index: number, option: CountryOption | null) => {
    updateRepresentative(index, "country_code", option ? option.value : "+91");
    setSelectedCountryCodes(prev => ({
      ...prev,
      [index]: option
    }));
  };

  // Initialize all data when client is available and offcanvas opens
  useEffect(() => {
    if (client && isOpen) {
      // Fetch designations first
      fetchDesignations().then(() => {
        // Set form data after designations are loaded
        const representatives = client.representatives || [];
        
        setFormData({
          id: client.id,
          client_full_name: client.client_full_name || "",
          client_short_name: client.client_short_name || "",
          address: client.address || "",
          gst_number: client.gst_number || "",
          state: client.state_circle?.state_name || client.state || "",
          state_circle_id: client.state_circle_id ?? '',
          is_active: client.is_active,
          representatives: representatives.map(rep => ({
            ...rep,
            country_code: rep.country_code || "+91", // Set default country code if not present
            designation_id: rep.designation_id ?? null,
          })),
        });

        // Set selected state
        if (client.state || client.state_circle_id) {
          setSelectedState({
            id: client.state_circle_id ?? client.state ?? '',
            value: client.state_circle_id ?? client.state ?? '',
            label: client.state_circle?.state_name || client.state || 'Select state'
          });
        }

        // Set selected country codes and designations for representatives
        const countryCodes: Record<number, any> = {};
        const designations: Record<number, any> = {};

        representatives.forEach((rep, index) => {
          // Set country code
          if (rep.country_code) {
            const countryOption = countryOptions.find(c => c.value === rep.country_code);
            if (countryOption) {
              countryCodes[index] = countryOption;
            } else {
              // Default to India if not found
              countryCodes[index] = countryOptions.find(c => c.value === "+91") || countryOptions[0];
            }
          } else {
            // Default to India if not present
            countryCodes[index] = countryOptions.find(c => c.value === "+91") || countryOptions[0];
          }

          // Set designation
          let designationOption = null;
          if (rep.designation_id) {
            designationOption = designationOptions.find(d => d.value === rep.designation_id);
          }
          if (!designationOption && rep.designation) {
            designationOption = designationOptions.find(d =>
              d.label.toLowerCase() === rep.designation.toLowerCase()
            );
          }
          if (designationOption) {
            designations[index] = designationOption;
          }
        });

        setSelectedCountryCodes(countryCodes);
        setSelectedDesignations(designations);
      });

      setErrors({});
      setTouched({});
      setRepresentativeErrors({});
      setDeletedRepresentativeIds([]);
    }
  }, [client, isOpen]);

  // Update designations when options load and client has representatives
  useEffect(() => {
    if (client && client.representatives && designationOptions.length > 0) {
      const designations: Record<number, any> = {};
      
      client.representatives.forEach((rep, index) => {
        let designationOption = null;
        if (rep.designation_id) {
          designationOption = designationOptions.find(d => d.value === rep.designation_id);
        }
        if (!designationOption && rep.designation) {
          designationOption = designationOptions.find(d =>
            d.label.toLowerCase() === rep.designation.toLowerCase()
          );
        }
        if (designationOption) {
          designations[index] = designationOption;
        }
      });
      
      setSelectedDesignations(designations);
    }
  }, [designationOptions, client]);

  const handleStateChange = (option: any | null) => {
    setSelectedState(option);
    setFormData((prev) => ({
      ...prev,
      state: option ? option.label : "",
      state_circle_id: option ? option.value : '',
    }));
    if (errors.state) {
      setErrors((prev) => ({
        ...prev,
        state: undefined,
      }));
    }
  };

  // Safe validation to prevent trim errors
  const validateField = (name: string, value: string) => {
    const trimmedValue = value ? value.trim() : "";
    
    switch (name) {
      case 'client_full_name':
        if (!trimmedValue) return 'Full name is required';
        if (trimmedValue.length < 2) return 'Full name must be at least 2 characters';
        if (trimmedValue.length > 100) return 'Full name must be less than 100 characters';
        break;
      case 'client_short_name':
        if (!trimmedValue) return 'Short name is required';
        if (trimmedValue.length < 2) return 'Short name must be at least 2 characters';
        if (trimmedValue.length > 50) return 'Short name must be less than 50 characters';
        break;
      case 'address':
        if (!trimmedValue) return 'Address is required';
        if (trimmedValue.length < 10) return 'Address must be at least 10 characters';
        if (trimmedValue.length > 500) return 'Address must be less than 500 characters';
        break;
      case 'gst_number':
        if (!trimmedValue) return 'GST number is required';
        const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
        if (!gstRegex.test(trimmedValue)) return 'Invalid GST number format';
        break;
      case 'state':
        if (!trimmedValue) return 'State is required';
        break;
    }
    return null;
  };

  const validateRepresentative = (rep: ClientRepresentative) => {
    const errors: Partial<ClientRepresentative> = {};

    // Safe trim checks
    const contactPerson = rep.contact_person ? rep.contact_person.trim() : "";
    const contactNumber = rep.contact_number ? rep.contact_number.trim() : "";
    const designation = rep.designation ? rep.designation.trim() : "";
    const location = rep.representative_location ? rep.representative_location.trim() : "";
    const hasDesignation = Boolean(designation) || rep.designation_id !== undefined && rep.designation_id !== null;

    if (!contactPerson) {
      errors.contact_person = 'Contact person is required';
    } else if (contactPerson.length < 2) {
      errors.contact_person = 'Name must be at least 2 characters';
    }

    if (!contactNumber) {
      errors.contact_number = 'Contact number is required';
    } else if (!/^[6-9]\d{9}$/.test(contactNumber)) {
      errors.contact_number = 'Enter valid 10-digit mobile number';
    }

    if (!hasDesignation) {
      errors.designation = 'Designation is required';
    }

    if (!location) {
      errors.representative_location = 'Location is required';
    }

    return errors;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name as keyof typeof errors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }

    // Validate field if touched
    if (touched[name]) {
      const error = validateField(name, value);
      setErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    setTouched(prev => ({
      ...prev,
      [name]: true
    }));

    const error = validateField(name, value);
    setErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  const handleToggleChange = () => {
    setFormData(prev => ({
      ...prev,
      is_active: !prev.is_active
    }));
  };

  // Representative handlers
  const addRepresentative = () => {
    const newIndex = formData.representatives?.length || 0;
    
    setFormData(prev => ({
      ...prev,
      representatives: [
        ...(prev.representatives || []),
        {
          contact_person: '',
          contact_number: '',
          designation: '',
          representative_location: '',
          country_code: '+91'
        }
      ]
    }));

    // Set default country code and empty designation for new representative
    const defaultCountry = countryOptions.find(c => c.value === "+91") || countryOptions[0];
    setSelectedCountryCodes(prev => ({
      ...prev,
      [newIndex]: defaultCountry
    }));
    
    setSelectedDesignations(prev => ({
      ...prev,
      [newIndex]: null
    }));
  };

  const removeRepresentative = (index: number) => {
    const repToRemove = formData.representatives?.[index];
    if (repToRemove?.id) {
      setDeletedRepresentativeIds((prev) =>
        prev.includes(repToRemove.id!) ? prev : [...prev, repToRemove.id!]
      );
    }
    setFormData(prev => ({
      ...prev,
      representatives: prev.representatives?.filter((_, i) => i !== index) || []
    }));
    setRepresentativeErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[index];
      return newErrors;
    });

    setSelectedCountryCodes(prev => {
      const next = { ...prev };
      delete next[index];
      return next;
    });

    setSelectedDesignations(prev => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  const updateRepresentative = (
    index: number,
    field: keyof ClientRepresentative,
    value: string | number | null
  ) => {
    setFormData(prev => ({
      ...prev,
      representatives: prev.representatives?.map((rep, i) =>
        i === index ? { ...rep, [field]: value } : rep
      ) || []
    }));

    // Clear error when user starts typing
    if (representativeErrors[index]?.[field]) {
      setRepresentativeErrors(prev => ({
        ...prev,
        [index]: {
          ...prev[index],
          [field]: undefined
        }
      }));
    }
  };

  const validateRepresentatives = () => {
    const errors: Record<number, Partial<ClientRepresentative>> = {};
    let hasErrors = false;

    formData.representatives?.forEach((rep, index) => {
      const repErrors = validateRepresentative(rep);
      if (Object.keys(repErrors).length > 0) {
        errors[index] = repErrors;
        hasErrors = true;
      }
    });

    setRepresentativeErrors(errors);
    return !hasErrors;
  };

  const hasChanges = (): boolean => {
    if (!client) return false;

    const clientFullNameChanged = formData.client_full_name.trim() !== (client.client_full_name || "").trim()
    const clientShortNameChanged = formData.client_short_name.trim() !== (client.client_short_name || "").trim()
    const addressChanged = formData.address.trim() !== (client.address || "").trim()
    const gstNumberChanged = formData.gst_number.trim() !== (client.gst_number || "").trim()
    const stateChanged = formData.state !== (client.state_circle?.state_name || client.state)
    const stateCircleChanged = (formData.state_circle_id ?? '') !== (client.state_circle_id ?? '')
    
    const statusChanged = formData.is_active !== client.is_active
    
    // Compare representatives
    const currentReps = formData.representatives || [];
    const clientReps = client.representatives || [];
    
    const repsChanged = JSON.stringify(currentReps) !== JSON.stringify(clientReps);

    return clientFullNameChanged || clientShortNameChanged || addressChanged || 
           gstNumberChanged || stateChanged || stateCircleChanged || statusChanged || repsChanged;
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Omit<EditClientFormData, 'id' | 'is_active' | 'representatives'>> = {};
    let isValid = true;

    // Validate required fields
    const fieldsToValidate = ['client_full_name', 'client_short_name', 'address', 'gst_number', 'state'];
    fieldsToValidate.forEach((field) => {
      const error = validateField(field, (formData[field as keyof EditClientFormData] as string) || '');
      if (error) {
        newErrors[field as keyof typeof newErrors] = error as any;
        isValid = false;
      }
    });

    setErrors(newErrors);
    setTouched({
      client_full_name: true,
      client_short_name: true,
      address: true,
      gst_number: true,
      state: true,
    });

    // Validate representatives
    if (!validateRepresentatives()) {
      isValid = false;
    }

    return isValid;
  };

  const deleteRemovedRepresentatives = async () => {
    if (!client || deletedRepresentativeIds.length === 0) return;
    try {
      await Promise.all(
        deletedRepresentativeIds.map((repId) =>
          axiosInstance.delete(`/clients/${client.id}/representatives/${repId}`)
        )
      );
      setDeletedRepresentativeIds([]);
    } catch (error) {
      console.error('Error deleting representatives:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (!hasChanges()) {
      onClose();
      return;
    }

    // Prepare data for submission
    const submitData: EditClientFormData = {
      id: formData.id,
      client_full_name: formData.client_full_name.trim(),
      client_short_name: formData.client_short_name.trim(),
      address: formData.address.trim(),
      gst_number: formData.gst_number.trim().toUpperCase(),
      state: formData.state,
      state_circle_id: formData.state_circle_id ? Number(formData.state_circle_id) : undefined,
      is_active: formData.is_active,
      representatives: formData.representatives?.map(rep => ({
        ...rep,
        contact_person: rep.contact_person ? rep.contact_person.trim() : "",
        contact_number: rep.contact_number ? rep.contact_number.trim() : "",
        designation: rep.designation ? rep.designation.trim() : "",
        designation_id: rep.designation_id ?? null,
        representative_location: rep.representative_location ? rep.representative_location.trim() : "",
        country_code: rep.country_code || "+91"
      }))
    };

    await deleteRemovedRepresentatives();
    onSubmit(submitData);
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isOpen || !client) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity" onClick={handleClose} />

      {/* Offcanvas */}
      <div className="overflow-y-auto fixed right-0 top-0 h-full w-full sm:max-w-4xl bg-white shadow-xl z-50 transform transition-transform overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
          <div>
            <h2 className="text-xl font-semibold text-gray-800">Edit Client</h2>
            <p className="text-sm text-gray-600 mt-1">Last updated: {formatDate(client.updated_at)}</p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-200 rounded transition-colors"
            disabled={loading}
          >
            <FaTimes className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-6">
            {/* Client Info */}
            <div className="bg-gray-50 border border-gray-200 rounded p-4 mb-6">
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                    <FaBuilding className="w-4 h-4 text-gray-600" />
                  </div>
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-gray-900">Client ID: {client.id}</h4>
                  <div className="text-xs text-gray-500 space-y-1">
                    <div>Created: {formatDate(client.created_at)}</div>
                    <div>Last Updated: {formatDate(client.updated_at)}</div>
                    <div>Projects: {client.client_projects?.length || 0}</div>
                    {client.suspended_at && (
                      <div className="text-red-600">Suspended: {formatDate(client.suspended_at)}</div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Client Basic Information */}
              <div className="bg-gray-50 border border-gray-200 rounded p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                  <FaBuilding className="w-5 h-5 text-blue-600" />
                  Client Information
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Full Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="client_full_name"
                      value={formData.client_full_name}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      placeholder="Enter client full name"
                      className={`w-full px-3 py-3 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${errors.client_full_name && touched.client_full_name
                          ? 'border-red-500 bg-red-50'
                          : 'border-gray-300'
                        }`}
                      disabled={loading}
                    />
                    {errors.client_full_name && touched.client_full_name && (
                      <p className="mt-1 text-sm text-red-600">{errors.client_full_name}</p>
                    )}
                  </div>

                  {/* Short Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Short Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="client_short_name"
                      value={formData.client_short_name}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      placeholder="Enter client short name"
                      className={`w-full px-3 py-3 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${errors.client_short_name && touched.client_short_name
                          ? 'border-red-500 bg-red-50'
                          : 'border-gray-300'
                        }`}
                      disabled={loading}
                    />
                    {errors.client_short_name && touched.client_short_name && (
                      <p className="mt-1 text-sm text-red-600">{errors.client_short_name}</p>
                    )}
                  </div>

                  {/* GST Number */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      GST Number <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <FaFileInvoice className="w-4 h-4 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        name="gst_number"
                        value={formData.gst_number}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        placeholder="e.g. 27AAAAA0000A1Z5"
                        className={`w-full pl-10 pr-3 py-3 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm font-mono ${errors.gst_number && touched.gst_number
                            ? 'border-red-500 bg-red-50'
                            : 'border-gray-300'
                          }`}
                        disabled={loading}
                        maxLength={15}
                      />
                    </div>
                    {errors.gst_number && touched.gst_number && (
                      <p className="mt-1 text-sm text-red-600">{errors.gst_number}</p>
                    )}
                  </div>

                  {/* State - Now using SelectWithSearch */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      State <span className="text-red-500">*</span>
                    </label>
                    <SelectWithSearch
                      value={selectedState}
                      onChange={handleStateChange}
                      placeholder="Select state"
                      apiEndpoint="/state-circle/list"
                      dataKey="states"
                      labelKey="state_name"
                      displayFormat={(item) => `${item.state_name}`}
                    />
                    {errors.state && touched.state && (
                      <p className="mt-1 text-sm text-red-600">{errors.state}</p>
                    )}
                  </div>
                </div>

                {/* Address */}
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Address <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    placeholder="Enter complete address"
                    rows={3}
                    className={`w-full px-3 py-3 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm resize-none ${errors.address && touched.address
                        ? 'border-red-500 bg-red-50'
                        : 'border-gray-300'
                      }`}
                    disabled={loading}
                  />
                  {errors.address && touched.address && (
                    <p className="mt-1 text-sm text-red-600">{errors.address}</p>
                  )}
                </div>

                {/* Status Toggle */}
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Status
                  </label>
                  <div className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded">
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${formData.is_active ? 'bg-green-500' : 'bg-red-500'
                        }`}></div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {formData.is_active ? 'Active' : 'Suspended'}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formData.is_active
                            ? 'Client is currently active and available'
                            : 'Client is currently suspended'
                          }
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleToggleChange}
                      disabled={loading}
                      className={`p-2 rounded transition-colors ${formData.is_active
                        ? 'text-green-600 hover:bg-green-50'
                        : 'text-red-600 hover:bg-red-50'
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {formData.is_active ? (
                        <FaToggleOn className="w-6 h-6" />
                      ) : (
                        <FaToggleOff className="w-6 h-6" />
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* Representatives Section */}
              <div className="bg-gray-50 border border-gray-200 rounded p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                    <FaUser className="w-5 h-5 text-blue-600" />
                    Client Representatives
                    <span className="text-sm font-normal text-gray-600">
                      ({formData.representatives?.length || 0})
                    </span>
                  </h3>
                  <button
                    type="button"
                    onClick={addRepresentative}
                    disabled={loading}
                    className="bg-blue-800 hover:bg-blue-950 text-white px-3 py-2 rounded text-sm flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FaPlus className="w-3 h-3" />
                    Add Representative
                  </button>
                </div>

                {!formData.representatives || formData.representatives.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <p className="text-sm">No representatives added yet</p>
                    <p className="text-xs mt-1">Click "Add Representative" to add client contacts</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {formData.representatives.map((rep, index) => (
                      <div key={index} className="bg-white border border-gray-200 rounded p-4">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="text-sm font-medium text-gray-800">
                            Representative #{index + 1}
                            {rep.id && (
                              <span className="text-xs text-gray-500 ml-2">ID: {rep.id}</span>
                            )}
                          </h4>
                          <button
                            type="button"
                            onClick={() => removeRepresentative(index)}
                            disabled={loading}
                            className="text-red-600 hover:text-red-800 p-1 hover:bg-red-50 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <FaTrash className="w-3 h-3" />
                          </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* Contact Person */}
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Contact Person <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <FaUser className="w-3 h-3 text-gray-400" />
                              </div>
                              <input
                                type="text"
                                value={rep.contact_person}
                                onChange={(e) => updateRepresentative(index, 'contact_person', e.target.value)}
                                placeholder="Enter contact person name"
                                className={`w-full pl-9 pr-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${representativeErrors[index]?.contact_person
                                    ? 'border-red-500 bg-red-50'
                                    : 'border-gray-300'
                                  }`}
                                disabled={loading}
                              />
                            </div>
                            {representativeErrors[index]?.contact_person && (
                              <p className="mt-1 text-xs text-red-600">
                                {representativeErrors[index].contact_person}
                              </p>
                            )}
                          </div>

                          {/* Contact Number with Country Code */}
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Contact Number <span className="text-red-500">*</span>
                            </label>
                            <div className="flex gap-2">
                              <div className="w-32">
                                <SearchableSelect
                                  value={selectedCountryCodes[index] || countryOptions.find(c => c.value === "+91") || null}
                                  onChange={(option: any) => updateRepresentativeCountryCode(index, option)}
                                  options={countryOptions}
                                  placeholder="Code"
                                  disabled={loading}
                                />
                              </div>
                              <div className="flex-1 relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                  <FaPhone className="w-3 h-3 text-gray-400" />
                                </div>
                                <input
                                  type="tel"
                                  value={rep.contact_number}
                                  onChange={(e) => updateRepresentative(index, 'contact_number', e.target.value.replace(/[^0-9]/g, ''))}
                                  placeholder="Enter 10-digit mobile number"
                                  className={`w-full pl-9 pr-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${representativeErrors[index]?.contact_number
                                      ? 'border-red-500 bg-red-50'
                                      : 'border-gray-300'
                                    }`}
                                  disabled={loading}
                                  maxLength={10}
                                />
                              </div>
                            </div>
                            {representativeErrors[index]?.contact_number && (
                              <p className="mt-1 text-xs text-red-600">
                                {representativeErrors[index].contact_number}
                              </p>
                            )}
                          </div>

                          {/* Designation - Now using SearchableSelect */}
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Designation <span className="text-red-500">*</span>
                            </label>
                            <SearchableSelect
                              value={selectedDesignations[index] || null}
                              onChange={(option: any) => updateRepresentativeDesignation(index, option)}
                              options={designationOptions}
                              placeholder="Select designation"
                              disabled={loading}
                              isLoading={designationLoading}
                              onSearch={handleDesignationSearch}
                              error={representativeErrors[index]?.designation}
                            />
                            {representativeErrors[index]?.designation && (
                              <p className="mt-1 text-xs text-red-600">
                                {representativeErrors[index].designation}
                              </p>
                            )}
                          </div>

                          {/* Representative Location */}
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Location <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <FaMapMarkerAlt className="w-3 h-3 text-gray-400" />
                              </div>
                              <input
                                type="text"
                                value={rep.representative_location}
                                onChange={(e) => updateRepresentative(index, 'representative_location', e.target.value)}
                                placeholder="Enter location"
                                className={`w-full pl-9 pr-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${representativeErrors[index]?.representative_location
                                    ? 'border-red-500 bg-red-50'
                                    : 'border-gray-300'
                                  }`}
                                disabled={loading}
                              />
                            </div>
                            {representativeErrors[index]?.representative_location && (
                              <p className="mt-1 text-xs text-red-600">
                                {representativeErrors[index].representative_location}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Form Actions */}
              <div className="flex items-center justify-between pt-6 border-t border-gray-200">
                <div className="text-sm text-gray-600">
                  {hasChanges() ? (
                    <span className="text-amber-600">You have unsaved changes</span>
                  ) : (
                    <span>No changes detected</span>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={handleClose}
                    disabled={loading}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded  hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading || !hasChanges()}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-800 border border-transparent rounded hover:bg-blue-950 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {loading && (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    )}
                    {loading ? 'Updating...' : 'Update Client'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};